import React, { createContext, useContext, useReducer, useEffect } from "react";
import { api, setAuthToken } from "../utils/api";

const AppContext = createContext();

function getOrCreateGuestId() {
  const existing = localStorage.getItem("guestId");
  if (existing) return existing;
  const id = `guest_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  localStorage.setItem("guestId", id);
  return id;
}

const storedUser = JSON.parse(localStorage.getItem("user") || "null");
if (storedUser && !storedUser.authToken) localStorage.removeItem("user");
setAuthToken(storedUser?.authToken);

const initialState = {
  shoppingList: [],
  favorites: [],
  favoriteIds: [],
  shoppingCount: 0,
  settings: null,
  user: storedUser?.authToken ? storedUser : null,
  guestId: getOrCreateGuestId(),
};

function reducer(state, action) {
  switch (action.type) {
    case "SET_SHOPPING":
      return { ...state, shoppingList: action.payload, shoppingCount: action.payload.length };
    case "SET_FAVORITES":
      return { ...state, favorites: action.payload.data, favoriteIds: action.payload.ids };
    case "ADD_SHOPPING":
      return {
        ...state,
        shoppingList: [...state.shoppingList, action.payload],
        shoppingCount: state.shoppingCount + 1,
      };
    case "TOGGLE_SHOPPING":
      return {
        ...state,
        shoppingList: state.shoppingList.map((item) =>
          item.id === action.payload.id ? action.payload : item
        ),
      };
    case "REMOVE_SHOPPING":
      return {
        ...state,
        shoppingList: state.shoppingList.filter((item) => item.id !== action.payload),
        shoppingCount: state.shoppingCount - 1,
      };
    case "CLEAR_CHECKED":
      return {
        ...state,
        shoppingList: state.shoppingList.filter((item) => !item.checked),
        shoppingCount: state.shoppingList.filter((item) => !item.checked).length,
      };
    case "TOGGLE_FAVORITE": {
      const { mealId, isFav } = action.payload;
      const newIds = isFav
        ? [...state.favoriteIds, mealId]
        : state.favoriteIds.filter((id) => id !== mealId);
      return { ...state, favoriteIds: newIds };
    }
    case "SET_USER":
      return { ...state, user: action.payload };
    case "SET_SETTINGS":
      return { ...state, settings: action.payload };
    default:
      return state;
  }
}

function applyDesignSettings(settings = {}) {
  const root = document.documentElement;
  const vars = {
    "--font-body": settings.bodyFont,
    "--font-heading": settings.headingFont,
    "--base-font-size": settings.baseFontSize,
    "--heading-scale": settings.headingScale,
    "--orange": settings.primaryColor,
    "--orange2": settings.primaryHoverColor,
    "--green": settings.successColor,
    "--button-text": settings.buttonTextColor,
    "--button-radius": settings.buttonRadius
  };
  Object.entries(vars).forEach(([key, value]) => {
    if (value) root.style.setProperty(key, value);
  });
}

export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const guestProfile = { guestId: initialState.guestId };
    api.getShoppingList(guestProfile).then((response) => dispatch({ type: "SET_SHOPPING", payload: response.data })).catch(() => {});
    api.getFavorites(guestProfile).then((response) => dispatch({ type: "SET_FAVORITES", payload: response })).catch(() => {});
    if (initialState.user?.id) {
      api.recordActivity().then((response) => {
        const user = { ...response.data, authToken: initialState.user.authToken };
        localStorage.setItem("user", JSON.stringify(user));
        dispatch({ type: "SET_USER", payload: user });
      }).catch(() => {});
    }
    api.getSettings().then((response) => {
      dispatch({ type: "SET_SETTINGS", payload: response.data });
      applyDesignSettings(response.data);
    }).catch(() => {});
  }, []);

  const usageProfile = () => ({
    userId: state.user?.id || "",
    guestId: state.guestId,
    isPremium: Boolean(state.user?.isPremium),
  });

  const actions = {
    addToShopping: async (name) => {
      try {
        const response = await api.addToShoppingList(name, { guestId: state.guestId });
        dispatch({ type: "ADD_SHOPPING", payload: response.data });
        return { success: true };
      } catch (error) {
        return { success: false, error: error.message };
      }
    },
    toggleShopping: async (id) => {
      const response = await api.toggleShoppingItem(id, { guestId: state.guestId });
      dispatch({ type: "TOGGLE_SHOPPING", payload: response.data });
    },
    removeShopping: async (id) => {
      await api.removeShoppingItem(id, { guestId: state.guestId });
      dispatch({ type: "REMOVE_SHOPPING", payload: id });
    },
    clearChecked: async () => {
      await api.clearCheckedItems({ guestId: state.guestId });
      dispatch({ type: "CLEAR_CHECKED" });
    },
    toggleFavorite: async (mealId) => {
      const response = await api.toggleFavorite(mealId, { guestId: state.guestId });
      dispatch({ type: "TOGGLE_FAVORITE", payload: { mealId, isFav: response.isFavorite } });
      api.getFavorites({ guestId: state.guestId }).then((fresh) => dispatch({ type: "SET_FAVORITES", payload: fresh })).catch(() => {});
      return response.isFavorite;
    },
    socialLogin: async (profile) => {
      const response = await api.socialLogin(profile);
      setAuthToken(response.data.authToken);
      localStorage.setItem("user", JSON.stringify(response.data));
      dispatch({ type: "SET_USER", payload: response.data });
      return response.data;
    },
    adminCodeLogin: async (payload) => {
      const response = await api.adminCodeLogin(payload);
      setAuthToken(response.data.authToken);
      localStorage.setItem("user", JSON.stringify(response.data));
      dispatch({ type: "SET_USER", payload: response.data });
      return response.data;
    },
    logout: () => {
      setAuthToken("");
      localStorage.removeItem("user");
      dispatch({ type: "SET_USER", payload: null });
    },
    createRecipe: async (payload) => {
      const response = await api.createRecipe({ ...payload, userId: state.user?.id || null });
      return response.data;
    },
    uploadRecipeImage: async (file) => {
      if (!state.user?.id) throw new Error("Giris gerekli");
      const response = await api.uploadRecipeImage(file, state.user.id);
      return response.data.image;
    },
    rateRecipe: async (recipeId, rating) => {
      if (!state.user?.id) throw new Error("Puan vermek için giriş yapın");
      const response = await api.rateRecipe(recipeId, { userId: state.user.id, rating });
      return response.data;
    },
    getMealConsumption: async (recipeId) => {
      if (!state.user?.id) return null;
      const response = await api.getMealConsumption(recipeId, state.user.id);
      return response.data;
    },
    markMealConsumed: async (recipeId, payload = {}) => {
      if (!state.user?.id) throw new Error("Giriş gerekli");
      const response = await api.markMealConsumed(recipeId, { userId: state.user.id, ...payload });
      return response.data;
    },
    unmarkMealConsumed: async (recipeId) => {
      if (!state.user?.id) throw new Error("Giriş gerekli");
      await api.unmarkMealConsumed(recipeId, state.user.id);
      return true;
    },
    updateProfile: async (profile) => {
      if (!state.user?.id) throw new Error("Giriş gerekli");
      const response = await api.updateUserProfile(state.user.id, profile);
      const user = { ...response.data, authToken: state.user.authToken };
      localStorage.setItem("user", JSON.stringify(user));
      dispatch({ type: "SET_USER", payload: user });
      return user;
    },
    getWeeklyProfile: async () => {
      if (!state.user?.id) throw new Error("Giriş gerekli");
      const response = await api.getWeeklyProfile(state.user.id);
      return response.data;
    },
    saveWeeklyProfile: async (profile) => {
      if (!state.user?.id) throw new Error("Giriş gerekli");
      const response = await api.saveWeeklyProfile(state.user.id, profile);
      const user = { ...response.data.user, authToken: state.user.authToken };
      localStorage.setItem("user", JSON.stringify(user));
      dispatch({ type: "SET_USER", payload: user });
      return response.data;
    },
    getWeeklyProfileReview: async (payload) => {
      if (!state.user?.id) throw new Error("Giriş gerekli");
      const response = await api.getWeeklyProfileReview(state.user.id, payload);
      return { ...(response.data || {}), cached: response.cached, ai: response.ai, fallback: response.fallback };
    },
    usageProfile,
    consumeUsage: async (usageType) => {
      const response = await api.consumeUsage(usageType, usageProfile());
      return response.data;
    },
    getAdminRecipes: async (status = "approved") => {
      if (!state.user?.id) throw new Error("Admin girisi gerekli");
      const response = await api.getAdminRecipes(state.user.id, status);
      return response.data;
    },
    getAdminTestUsers: async (filter = "all") => {
      if (!state.user?.id) throw new Error("Admin girisi gerekli");
      const response = await api.getAdminTestUsers(state.user.id, filter);
      return response.data;
    },
    moderateRecipe: async (recipeId, status) => {
      if (!state.user?.id) throw new Error("Admin girisi gerekli");
      const response = await api.moderateRecipe(recipeId, { userId: state.user.id, status });
      return response.data;
    },
    updateAdminRecipeContent: async (recipeId, content) => {
      if (!state.user?.id) throw new Error("Admin girisi gerekli");
      const response = await api.updateAdminRecipeContent(recipeId, { userId: state.user.id, ...content });
      return response.data;
    },
    updateAdminRecipeMedia: async (recipeId, media) => {
      if (!state.user?.id) throw new Error("Admin girisi gerekli");
      const response = await api.updateAdminRecipeMedia(recipeId, { userId: state.user.id, ...media });
      return response.data;
    },
    getAdminCopyrightReports: async (status = "new") => {
      if (!state.user?.id) throw new Error("Admin girisi gerekli");
      const response = await api.getAdminCopyrightReports(state.user.id, status);
      return response.data;
    },
    updateAdminCopyrightReport: async (reportId, status) => {
      if (!state.user?.id) throw new Error("Admin girisi gerekli");
      const response = await api.updateAdminCopyrightReport(reportId, { userId: state.user.id, status });
      return response.data;
    },
    importThemealdbRecipes: async (payload) => {
      if (!state.user?.id) throw new Error("Admin girisi gerekli");
      const response = await api.importThemealdbRecipes({ userId: state.user.id, ...payload });
      return response.data;
    },
    uploadAdminRecipeImage: async (recipeId, file) => {
      if (!state.user?.id) throw new Error("Admin girisi gerekli");
      const response = await api.uploadAdminRecipeImage(recipeId, file, state.user.id);
      return response.data;
    },
    updateAdminSettings: async (settings) => {
      if (!state.user?.id) throw new Error("Admin girisi gerekli");
      const response = await api.updateAdminSettings({ userId: state.user.id, settings });
      dispatch({ type: "SET_SETTINGS", payload: response.data });
      applyDesignSettings(response.data);
      return response.data;
    },
  };

  return (
    <AppContext.Provider value={{ state, actions }}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
